import { AlertTriangle } from 'lucide-react';
import { MEMBER_GENDER_LABELS, type MemberGender } from '@bm/contracts';
import { genderBgClass } from '../../lib/gender-colors';

export interface LineupPlayerCardData {
  memberId: string;
  name: string;
  teamLabel?: string | null;
  gender: MemberGender | null;
  appearanceCount: number;
  maxAppearances: number;
  /** Noch nicht für dieses Spiel abgestimmt (z. B. über "Spieler
   * hinzufügen" manuell ergänzt) — überschreibt die Geschlechter-Färbung
   * mit Grau + Warndreieck, bis eine echte Rückmeldung vorliegt. */
  notResponded?: boolean;
}

/** Kompakte Karte für "Verfügbare Spieler" — reines Anzeige-/Drag-Element auf
 * dem Desktop; die eigentliche, überall funktionierende Zuweisung passiert
 * über PlayerSelectDrawer (Tap auf einen Slot), Drag&Drop ist hier nur ein
 * optionaler Beschleuniger für Maus-Nutzer. */
export function LineupPlayerCard({
  player,
  draggable = false,
  onDragStart,
  onDragEnd,
}: {
  player: LineupPlayerCardData;
  draggable?: boolean;
  onDragStart?: () => void;
  onDragEnd?: () => void;
}) {
  const atLimit = player.appearanceCount >= player.maxAppearances;
  const colorClass = player.notResponded ? 'border-slate-200 bg-slate-100' : genderBgClass(player.gender);
  return (
    <div
      draggable={draggable}
      onDragStart={(e) => {
        e.dataTransfer.setData('text/plain', player.memberId);
        e.dataTransfer.effectAllowed = 'copy';
        onDragStart?.();
      }}
      onDragEnd={onDragEnd}
      className={`flex min-w-0 items-center justify-between gap-2 rounded-lg border px-3 py-2 text-sm shadow-sm ${colorClass} ${
        draggable ? 'cursor-grab active:cursor-grabbing' : ''
      } ${atLimit ? 'opacity-60' : ''}`}
      title={
        player.notResponded
          ? `${player.name} — hat für dieses Spiel noch nicht abgestimmt`
          : atLimit
            ? `${player.name} — maximale Einsatzzahl (${player.maxAppearances}) erreicht`
            : player.name
      }
    >
      <div className="min-w-0">
        <div className="flex items-center gap-1 truncate font-medium text-slate-800">
          {player.notResponded && (
            <AlertTriangle size={13} className="shrink-0 text-red-500" aria-hidden />
          )}
          <span className="truncate">{player.name}</span>
        </div>
        <div className="flex flex-wrap items-center gap-x-1.5 text-xs text-slate-500">
          {player.gender && <span>{MEMBER_GENDER_LABELS[player.gender]}</span>}
          {player.teamLabel && <span>· {player.teamLabel}</span>}
          {player.notResponded && <span className="text-red-500">· noch nicht abgestimmt</span>}
        </div>
      </div>
      <span
        className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${
          player.appearanceCount === 0 ? 'bg-slate-200/70 text-slate-500' : 'bg-brand-accent/10 text-brand-accent'
        }`}
      >
        {player.appearanceCount} {player.appearanceCount === 1 ? 'Einsatz' : 'Einsätze'}
      </span>
    </div>
  );
}
